/* eslint-disable tss-unused-classes/unused-classes */
import React, { useEffect, useState } from 'react';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, SvgIcon } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ArrowIcon from 'app/assets/images/Credentialing/PersonalInfo/arrow.svg';
import { getSearchFilterOptions } from 'app/services/SharedServices/SharedServices';
import { trackException } from 'app-insights/appInsightsTracking';
import { ExceptionType } from 'app/enums/Common';
import { theme } from 'styles/global-styles';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles()(() => ({
  element: {
    height: '100%',
    width: '100%',
    maxWidth: '350px',
    background: theme.palette.system.white,
  },
  label: {
    fontSize: 16,
  },
  root: {
    border: `1px solid ${theme.palette.system.shadesofCeleste}`,
    overflow: 'hidden',
    borderRadius: 4,
    fontSize: 16,
    backgroundColor: theme.palette.system.white,
    '&:hover': {
      borderColor: theme.palette.system.shadesofDarkGray,
      backgroundColor: theme.palette.system.white,
    },
    '&:focus': {
      borderColor: '#aaa',
      borderRadius: 4,
      backgroundColor: theme.palette.system.white,
    },
  },
  dropdownStyle: {
    backgroundColor: theme.palette.system.white,
    border: `1px solid ${theme.palette.system.shadesofGray}`,
    borderRadius: 4,
    boxShadow: 'none',
    marginTop: '2px',
    maxwidth: '200',
    maxHeight: 300,
  },
  disabled: {
    border: `1px solid ${theme.palette.system.shadesofWhisper}`,
    overflow: 'hidden',
    borderRadius: 4,
    fontSize: 16,
    backgroundColor: theme.palette.system.shadesofWhisper,
    '&:hover': {
      borderColor: theme.palette.system.shadesofCeleste,
      backgroundColor: theme.palette.system.shadesofWhisper,
    },
  },
  reqTextareastyle: {
    border: `thin #${theme.palette.system.deepSkyBlue} solid !important`,
  },
  arrowIcon: {
    marginRight: '10px',
    marginLeft: '10px',
    marginTop: '4px',
  },
}));

interface IFileOutReason {
  id: number;
  disableEdit: boolean;
  description: string;
}

export const FileOutReason = ({
  handleFileOutChange,
  name,
  title,
  disableEdit,
  value,
  errorField = false,
  editOrderPage = false,
  hideHelperText = false,
}: {
  handleFileOutChange: (event: any) => void;
  name: string;
  title: string;
  disableEdit: boolean;
  value: number;
  errorField?: boolean;
  editOrderPage?: boolean;
  hideHelperText?:boolean;
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [fileOutReasonList, setFileOutReasonList] = useState<IFileOutReason[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const list = await getSearchFilterOptions('orderdecrementreason');
        setFileOutReasonList(
          list.map(item => {
            return {
              id: item.value,
              description: item.name,
            };
          }),
        );
      } catch (error) {
        trackException({
          exception: error,
          properties: {
            name: ExceptionType.CommonUIError,
            functionName: 'fetchData',
            area: 'src/app/components/Order/OrderDetails/OrderAtAGlance/FileOutReason.tsx',
          },
        });
      }
    };
    fetchData();
  }, []);

  return (
    <FormControl disabled={disableEdit} variant="filled" className={classes.element}>
      <InputLabel className={classes.label} style={{ color: errorField ? theme.palette.system.red : 'inherit' }}>
        {title}
      </InputLabel>
      <Select
        style={{
          borderBottom: `2px solid ${errorField ? theme.palette.system.red : theme.palette.system.blackGray}`,
        }}
        data-testid="test drop down element"
        value={value}
        disableUnderline
        classes={classes}
        error={errorField}
        MenuProps={{
          classes: { paper: classes.dropdownStyle },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }}
        onChange={handleFileOutChange}
        IconComponent={
          editOrderPage
            ? props => <img src={ArrowIcon} className={classes.arrowIcon} {...props} />
            : ExpandMoreOutlinedIcon
        }
      >
        {fileOutReasonList.map(item => (
          <MenuItem 
            style={{ 
              height: 30,
              maxWidth: '330px'
            }} 
            key={item.id} 
            value={item.id}
            title={item.description}
          >
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                maxWidth: '100%'
              }}
            >
              {item.description}
            </span>
          </MenuItem>
        ))}
      </Select>
      {errorField && !hideHelperText ? (
        <FormHelperText style={{ color: theme.palette.system.red }}> {t('order.requiredMessage')}</FormHelperText>
      ) : null}
    </FormControl>
  );
};
